import { createContext, useState, useCallback, type ReactNode } from 'react';
import { useCopilotChat, type CopilotChat } from '../hooks/use-copilot-chat';

const STORAGE_KEY = 'ai-copilot-drawer-width';
const MIN_WIDTH = 320;
const DEFAULT_RATIO = 0.3;
const MAX_RATIO = 0.55;

function clampWidth(width: number): number {
  return Math.max(MIN_WIDTH, Math.min(width, Math.round(window.innerWidth * MAX_RATIO)));
}

function getInitialWidth(): number {
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (stored) {
    const parsed = Number(stored);
    if (!Number.isNaN(parsed) && parsed > 0) return clampWidth(parsed);
  }
  return Math.round(window.innerWidth * DEFAULT_RATIO);
}

export type AiCopilotContextType = {
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  clearChat: () => void;
  chat: CopilotChat;
  drawerWidth: number;
  setDrawerWidth: (width: number) => void;
  suggestions?: string[];
  setSuggestions: (suggestions: string[] | undefined) => void;
};

export const AiCopilotContext = createContext<AiCopilotContextType | undefined>(undefined);

export const AiCopilotProvider: React.FC<{ children: ReactNode; suggestions?: string[]; reportContext?: string }> = ({ children, suggestions: initialSuggestions, reportContext }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [drawerWidth, setDrawerWidthRaw] = useState(getInitialWidth);
  const [suggestions, setSuggestions] = useState<string[] | undefined>(initialSuggestions);
  const chat = useCopilotChat(reportContext);

  const openDrawer = useCallback(() => setIsOpen(true), []);
  const closeDrawer = useCallback(() => setIsOpen(false), []);
  const clearChat = useCallback(() => chat.setMessages([]), [chat]);

  const setDrawerWidth = useCallback((width: number) => {
    const clamped = clampWidth(width);
    setDrawerWidthRaw(clamped);
    sessionStorage.setItem(STORAGE_KEY, String(clamped));
  }, []);

  return (
    <AiCopilotContext.Provider value={{ isOpen, openDrawer, closeDrawer, clearChat, chat, drawerWidth, setDrawerWidth, suggestions, setSuggestions }}>
      {children}
    </AiCopilotContext.Provider>
  );
};
